import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  Paper
} from '@mui/material'
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Clear as ClearIcon,
  BarChart as AnalyticsIcon,
  Image as ImageIcon,
  Storage as StorageIcon
} from '@mui/icons-material'
import { cloudinaryAPI } from '../services/api'

function CloudinaryManagement() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState(0)
  const [stats, setStats] = useState({
    usage: 0,
    limit: 0,
    images: 0,
    transformations: 0
  })
  const [queue, setQueue] = useState([])
  const [processing, setProcessing] = useState(false)
  const [uploadDialog, setUploadDialog] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      setError('')
      
      const [statsResponse, queueResponse] = await Promise.allSettled([
        cloudinaryAPI.getStats(),
        cloudinaryAPI.getQueue()
      ])

      if (statsResponse.status === 'fulfilled' && statsResponse.value.success) {
        setStats(statsResponse.value.data)
      }

      if (queueResponse.status === 'fulfilled' && queueResponse.value.success) {
        setQueue(queueResponse.value.data)
      }

    } catch (error) {
      console.error('Error loading Cloudinary data:', error)
      setError('Failed to load Cloudinary data')
    } finally {
      setLoading(false)
    }
  }

  const handleProcessQueue = async (limit = 10) => {
    try {
      setProcessing(true)
      setError('')
      setSuccess('')
      
      const response = await cloudinaryAPI.processQueue(limit)
      if (response.success) {
        setSuccess(`Processed ${response.data.processed} items from queue`)
        await loadData()
      } else {
        setError('Failed to process queue')
      }
    } catch (error) {
      console.error('Error processing queue:', error)
      setError('Failed to process queue')
    } finally {
      setProcessing(false)
    }
  }

  const handleClearQueue = async () => {
    if (window.confirm('Are you sure you want to clear the entire queue?')) {
      try {
        setError('')
        setSuccess('')
        
        const response = await cloudinaryAPI.clearQueue()
        if (response.success) {
          setSuccess('Queue cleared successfully')
          await loadData()
        } else {
          setError('Failed to clear queue')
        }
      } catch (error) {
        console.error('Error clearing queue:', error)
        setError('Failed to clear queue')
      }
    }
  }

  const handleReprocessFailed = async () => {
    try {
      setError('')
      setSuccess('')
      
      const response = await cloudinaryAPI.reprocessFailed()
      if (response.success) {
        setSuccess(`Reprocessed ${response.data.reprocessed} failed items`)
        await loadData()
      } else {
        setError('Failed to reprocess failed items')
      }
    } catch (error) {
      console.error('Error reprocessing failed items:', error)
      setError('Failed to reprocess failed items')
    }
  }

  const handleFileUpload = async () => {
    if (!selectedFile) return

    try {
      setError('')
      setSuccess('')
      
      const response = await cloudinaryAPI.upload(selectedFile)
      if (response.success) {
        setSuccess('File uploaded successfully')
        setUploadDialog(false)
        setSelectedFile(null)
        await loadData()
      } else {
        setError('Failed to upload file')
      }
    } catch (error) {
      console.error('Error uploading file:', error)
      setError('Failed to upload file')
    }
  }

  const handleOptimizeImages = async () => {
    try {
      setError('')
      setSuccess('')
      
      const response = await cloudinaryAPI.optimizeImages()
      if (response.success) {
        setSuccess(`Optimized ${response.data.optimized} images`)
        await loadData()
      } else {
        setError('Failed to optimize images')
      }
    } catch (error) {
      console.error('Error optimizing images:', error)
      setError('Failed to optimize images')
    }
  }

  const usagePercentage = stats.limit > 0 ? (stats.usage / stats.limit) * 100 : 0

  const StatsCard = ({ title, value, subtitle, icon, color = 'primary' }) => (
    <Card className="hover-card">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ color: `${color}.main`, mr: 2 }}>
            {icon}
          </Box>
          <Typography variant="h6">{title}</Typography>
        </Box>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  )

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Cloudinary Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={() => setUploadDialog(true)}
          >
            Upload Image
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Storage Used"
            value={`${(stats.usage / 1024 / 1024).toFixed(1)} MB`}
            subtitle={`${usagePercentage.toFixed(1)}% of limit`}
            icon={<StorageIcon />}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Total Images"
            value={stats.images}
            subtitle="Images stored"
            icon={<ImageIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Transformations"
            value={stats.transformations}
            subtitle="This month"
            icon={<AnalyticsIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Queue Items"
            value={queue.length}
            subtitle="Pending processing"
            icon={<PlayArrowIcon />}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Usage Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Storage Usage
          </Typography>
          <LinearProgress
            variant="determinate"
            value={usagePercentage}
            color={usagePercentage > 80 ? 'error' : usagePercentage > 60 ? 'warning' : 'success'}
            sx={{ height: 10, borderRadius: 5, mb: 1 }}
          />
          <Typography variant="body2" color="text.secondary">
            {(stats.usage / 1024 / 1024).toFixed(1)} MB of {(stats.limit / 1024 / 1024).toFixed(1)} MB used
          </Typography>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label="Queue Management" />
          <Tab label="Image Operations" />
          <Tab label="Analytics" />
        </Tabs>

        <TabPanel value={activeTab} index={0}>
          {/* Queue Management */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processing Queue ({queue.length} items)
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                startIcon={processing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                onClick={() => handleProcessQueue(10)}
                disabled={processing || queue.length === 0}
                sx={{ mr: 2 }}
              >
                Process Queue (10)
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleReprocessFailed}
                sx={{ mr: 2 }}
              >
                Reprocess Failed
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<ClearIcon />}
                onClick={handleClearQueue}
                disabled={queue.length === 0}
              >
                Clear Queue
              </Button>
            </Box>
          </Box>

          {queue.length > 0 ? (
            <List>
              {queue.slice(0, 10).map((item, index) => (
                <ListItem key={index} divider>
                  <ListItemText
                    primary={item.image_url || item.article_title}
                    secondary={`Status: ${item.status} | Created: ${new Date(item.created_at).toLocaleString()}`}
                  />
                  <ListItemSecondaryAction>
                    <Chip
                      label={item.status}
                      color={
                        item.status === 'completed' ? 'success' :
                        item.status === 'failed' ? 'error' : 'default'
                      }
                      size="small"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography color="text.secondary">No items in queue</Typography>
          )}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Image Operations */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Bulk Operations
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    startIcon={<AnalyticsIcon />}
                    onClick={handleOptimizeImages}
                    sx={{ mb: 2 }}
                  >
                    Optimize All Images
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Optimize all images for better performance and reduced storage usage.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cleanup Operations
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    fullWidth
                    startIcon={<DeleteIcon />}
                    sx={{ mb: 2 }}
                  >
                    Remove Unused Images
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Remove images that are no longer referenced by any articles.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Analytics */}
          <Typography variant="h6" gutterBottom>
            Usage Analytics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Monthly Usage Trend
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Analytics charts would go here
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Top Transformations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Most used image transformations
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Image</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setSelectedFile(e.target.files[0])}
              style={{ marginBottom: 16 }}
            />
            {selectedFile && (
              <Typography variant="body2" color="text.secondary">
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>Cancel</Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={!selectedFile}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CloudinaryManagement